import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getCustomerRewards } from '../services/customersApi';
import { createLogger } from '../../../shared/config/logger';

const logger = createLogger('CustomerPassPage');

/** e.g. "Joe's rewards" — falls back if name is missing */
function merchantRewardsHeading(merchantName) {
    const n = (merchantName || '').trim();
    if (!n) {
        return 'Your rewards';
    }
    return `${n}'s rewards`;
}

export default function CustomerPassPage() {
    const { passId } = useParams();
    const [customer, setCustomer] = useState(null);
    const [merchant, setMerchant] = useState({ name: '', logo_url: null });
    const [rewards, setRewards] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchCustomerData = async () => {
            if (!passId) {return;}
            try {
                const res = await getCustomerRewards(passId);
                setCustomer(res.data.customer);
                setMerchant(res.data.merchant || { name: '', logo_url: null });
                setRewards(res.data.rewards || []);
            } catch (err) {
                logger.error('Failed to fetch customer data', { error: err, passId });
            } finally {
                setLoading(false);
            }
        };

        fetchCustomerData();
    }, [passId]);

    if (loading) {
        return (
            <div className="min-h-screen bg-[#0D0221] text-white flex items-center justify-center font-sans">
                <p className="text-gray-300">Loading your rewards…</p>
            </div>
        );
    }
    if (!customer) {
        return (
            <div className="min-h-screen bg-[#0D0221] text-white flex items-center justify-center font-sans">
                <p className="text-red-400">Pass not found.</p>
            </div>
        );
    }

    const heading = merchantRewardsHeading(merchant.name);
    const initial = (merchant.name || customer.name || '?').trim().charAt(0).toUpperCase() || '?';

    return (
        <div className="min-h-screen bg-[#0D0221] text-white font-sans pb-12">
            <div className="max-w-3xl mx-auto px-4 pt-8 pb-6">
                <header className="flex flex-col items-center text-center border-b border-white/10 pb-8">
                    {merchant.logo_url ? (
                        <img
                            src={merchant.logo_url}
                            alt={merchant.name ? `${merchant.name} logo` : 'Store logo'}
                            className="h-24 w-24 rounded-2xl object-cover shadow-xl ring-1 ring-white/15 mb-5 bg-[#1A1032]"
                        />
                    ) : (
                        <div
                            className="h-24 w-24 rounded-2xl flex items-center justify-center text-3xl font-bold text-[#0D0221] bg-[#00FFD0] shadow-xl mb-5"
                            aria-hidden
                        >
                            {initial}
                        </div>
                    )}
                    <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-white">{heading}</h1>
                    <p className="mt-2 text-gray-300">
                        Hi, <span className="text-white font-medium">{customer.name}</span>
                    </p>
                    <p className="mt-3 inline-flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-sm text-[#00FFD0]">
                        <span>{customer.points} points</span>
                        <span className="text-white/30" aria-hidden>
                            ·
                        </span>
                        <span>{customer.visits} visits</span>
                    </p>
                    <p className="mt-4 max-w-md text-xs text-gray-500 leading-relaxed">
                        Below are the rewards this business offers. Eligibility is based on your points, visits, and
                        any rules they have set.
                    </p>
                </header>

                <h2 className="sr-only">All rewards</h2>
                {rewards.length === 0 ? (
                    <p className="text-center text-gray-400 mt-10">No rewards are available yet. Check back soon.</p>
                ) : (
                    <ul className="mt-8 grid gap-4 sm:grid-cols-1 md:grid-cols-2">
                        {rewards.map((reward) => {
                            const eligible =
                                reward.eligible === true ||
                                (reward.eligible === undefined &&
                                    ((reward.type === 'points' &&
                                        customer.points >= (reward.cost ?? 0)) ||
                                        (reward.type === 'visits' &&
                                            customer.visits >= (reward.cost ?? reward.required_visits ?? 0))));

                            return (
                                <li
                                    key={reward.id}
                                    className="bg-[#1A1032] rounded-2xl p-5 shadow-lg border border-white/5 flex flex-col"
                                >
                                    <div className="flex gap-4 flex-1">
                                        <div className="min-w-0 flex-1">
                                            <h3 className="text-lg font-semibold text-white leading-snug">
                                                {reward.title}
                                            </h3>
                                            {reward.description ? (
                                                <p className="mt-2 text-sm text-gray-400 leading-relaxed">
                                                    {reward.description}
                                                </p>
                                            ) : null}
                                            <p className="mt-3 text-sm font-medium text-[#FF4DA6]">
                                                {reward.type === 'birthday'
                                                    ? 'Birthday reward'
                                                    : `Cost: ${reward.cost ?? reward.required_visits ?? '-'} ${
                                                          reward.type === 'points' ? 'points' : 'visits'
                                                      }`}
                                            </p>
                                        </div>
                                        {reward.image ? (
                                            <img
                                                src={reward.image}
                                                alt={reward.title || 'Reward image'}
                                                className="w-16 h-16 rounded-xl object-cover shrink-0"
                                            />
                                        ) : null}
                                    </div>
                                    <div
                                        role="status"
                                        className={`mt-4 py-2.5 px-3 rounded-xl text-sm font-semibold text-center cursor-default select-none ${
                                            eligible
                                                ? 'bg-[#00FFD0]/15 text-[#00FFD0] border border-[#00FFD0]/30'
                                                : 'bg-white/5 text-gray-400 border border-white/10'
                                        }`}
                                        aria-label={
                                            eligible
                                                ? 'You are eligible for this reward'
                                                : 'You are not yet eligible for this reward'
                                        }
                                    >
                                        {eligible ? 'Eligible' : 'Not eligible'}
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>
        </div>
    );
}
